import { createSlice } from '@reduxjs/toolkit';
import isEqual from 'lodash/isEqual';

import { getPortalLabel } from '@/hooks/usePortalLabel';
import { getAddressValidationFeatureKeyForPortal } from '@/hooks/features/useFeatureAddressValidation';

import {
  EstimatedPickupTime,
  OnHoldTripLegApproveDenyAction,
  Reservation,
  ReservationCreate,
  ReservationModelState,
  ReservationValidations,
  StandingOrder,
  TripLeg,
} from '@/data/models/reservation.model';
import {
  ReservationEventDetail,
  ReservationEventType,
} from '@/data/events/reservation';
import { TripLegConfidence } from '@/data/models/trip-leg-confidence.model';
import {
  TripLegConfidenceRequest,
  TripLegConfidenceResponse,
} from '@/data/requests/trip-leg-confidence';

import * as passengerSlice from '@/data/passenger.slice';
import * as tripLegSlice from '@/data/tripleg.slice';
import * as uiSlice from '@/data/ui.slice';
import reservationService from '@/services/reservationService';
import triplegService from '@/services/triplegService';

import { filterConfidenceErrorsForUnsetPayload } from '@/components/reservations/utils/filterConfidenceErrorsForUnsetPayload';
import { filterAddressErrorsForOneSidedTripLegs } from '@/components/reservations/utils/filterAddressErrorsForOneSidedTripLegs';

interface ReservationState {
  savingReservation: boolean;
  savedReservation: boolean;
  currentReservation: ReservationCreate | null;
  loadingReservation: boolean;
  loadedReservation: boolean;
  canceledReservation: boolean;
  savingTripLeg: boolean;
  savedTripLeg: boolean;
  loadingTripLeg: boolean;
  loadedTripLeg: boolean;
  currentTripLeg: TripLeg | null;
  loadingAllTripLegs: boolean;
  loadedAllTripLegs: boolean;
  canceledTripLeg: boolean;
  allTripLegs: TripLeg[];
  loadingDraftTripLegs: boolean;
  loadedDraftTripLegs: boolean;
  draftTripLegs: TripLeg[];
  loadingReopenRejectedTripLeg: boolean;
  loadedReopenRejectedTripLeg: boolean;
  loadedReopenEditTripLegTime: boolean;
  loadingReservationValidations: boolean;
  loadedReservationValidations: boolean;
  reservationValidations: ReservationValidations | null;
  loadedBenefitsSummary: boolean;
  loadingBenefitSummary: boolean;
  benefitSummary: {
    [key: string]: {
      [key: string]: number;
    };
  };
  narrative: string | null;
  narrativeMetadata: any;
  reservationEventEmitted: {
    detailType: ReservationEventType;
    detail: ReservationEventDetail;
  } | null;
  // Marker for "the user just created this reservation", set synchronously
  // once the create call resolves. Persists until ReservationAccordion has
  // had a chance to auto-expand it, even if that accordion doesn't mount
  // until after this is set (the list still needs to refetch/re-render).
  justCreatedReservation: {
    passengerUUID: string;
    reservationReferenceID: string;
  } | null;
  estimatedTripTime: EstimatedPickupTime | null;
  reservationConfidence: TripLegConfidenceResponse[];
  // TODO(dead code): only used by `validateTripLegAddressAsync` (orphaned) — remove together.
  requestedReservationConfidence: {
    request: TripLegConfidenceRequest;
    response: TripLegConfidenceResponse | null;
  }[];
  loadingReservationConfidence: boolean;
  loadedReservationConfidence: boolean;
}

export const initialState: ReservationState = {
  savingReservation: false,
  savedReservation: false,
  currentReservation: null,
  loadingReservation: false,
  loadedReservation: false,
  canceledReservation: false,
  savingTripLeg: false,
  savedTripLeg: false,
  loadingTripLeg: false,
  loadedTripLeg: false,
  currentTripLeg: null,
  loadingAllTripLegs: false,
  loadedAllTripLegs: false,
  canceledTripLeg: false,
  allTripLegs: [],
  loadingDraftTripLegs: false,
  loadedDraftTripLegs: false,
  draftTripLegs: [],
  loadingReopenRejectedTripLeg: false,
  loadedReopenRejectedTripLeg: false,
  loadedReopenEditTripLegTime: false,
  loadingReservationValidations: false,
  loadedReservationValidations: false,
  reservationValidations: null,
  loadedBenefitsSummary: false,
  loadingBenefitSummary: false,
  benefitSummary: {},
  narrative: null,
  narrativeMetadata: {},
  reservationEventEmitted: null,
  justCreatedReservation: null,
  estimatedTripTime: null,
  reservationConfidence: [],
  requestedReservationConfidence: [],
  loadingReservationConfidence: false,
  loadedReservationConfidence: false,
};

export const slice = createSlice({
  name: 'reservation',
  initialState,
  reducers: {
    setSavingReservation: (
      state: ReservationState,
      action: { type: string; payload: boolean },
    ) => {
      state.savingReservation = action.payload;
    },
    setSavedReservation: (
      state: ReservationState,
      action: { type: string; payload: boolean },
    ) => {
      state.savedReservation = action.payload;
    },
    setCurrentReservation: (
      state: ReservationState,
      action: { type: string; payload: ReservationCreate | null },
    ) => {
      state.currentReservation = action.payload;
    },
    setLoadingReservation: (
      state: ReservationState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingReservation = action.payload;
    },
    setLoadedReservation: (
      state: ReservationState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedReservation = action.payload;
    },
    setCanceledReservation: (
      state: ReservationState,
      action: { type: string; payload: boolean },
    ) => {
      state.canceledReservation = action.payload;
    },
    setSavingTripLeg: (
      state: ReservationState,
      action: { type: string; payload: boolean },
    ) => {
      state.savingTripLeg = action.payload;
    },
    setSavedTripLeg: (
      state: ReservationState,
      action: { type: string; payload: boolean },
    ) => {
      state.savedTripLeg = action.payload;
    },
    setCurrentTripLeg: (
      state: ReservationState,
      action: { type: string; payload: TripLeg | null },
    ) => {
      state.currentTripLeg = action.payload;
    },
    setLoadingTripLeg: (
      state: ReservationState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingTripLeg = action.payload;
    },
    setLoadedTripLeg: (
      state: ReservationState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedTripLeg = action.payload;
    },
    setAllTripLegs: (
      state: ReservationState,
      action: { type: string; payload: TripLeg[] },
    ) => {
      state.allTripLegs = action.payload;
    },
    setLoadingAllTripLegs: (
      state: ReservationState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingAllTripLegs = action.payload;
    },
    setLoadedAllTripLegs: (
      state: ReservationState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedAllTripLegs = action.payload;
    },
    setCanceledTripLeg: (
      state: ReservationState,
      action: { type: string; payload: boolean },
    ) => {
      state.canceledTripLeg = action.payload;
    },
    setDraftTripLegs: (
      state: ReservationState,
      action: { type: string; payload: TripLeg[] },
    ) => {
      state.draftTripLegs = action.payload;
    },
    setLoadingDraftTripLegs: (
      state: ReservationState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingDraftTripLegs = action.payload;
    },
    setLoadedDraftTripLegs: (
      state: ReservationState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedDraftTripLegs = action.payload;
    },
    setLoadingReopenRejectedTripLeg: (
      state: ReservationState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingReopenRejectedTripLeg = action.payload;
    },
    setLoadedReopenRejectedTripLeg: (
      state: ReservationState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedReopenRejectedTripLeg = action.payload;
    },
    setLoadedReopenEditTripLegTime: (
      state: ReservationState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedReopenEditTripLegTime = action.payload;
    },
    setLoadingReservationValidations: (
      state: ReservationState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingReservationValidations = action.payload;
    },
    setLoadedReservationValidations: (
      state: ReservationState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedReservationValidations = action.payload;
    },
    setReservationValidations: (
      state: ReservationState,
      action: { type: string; payload: ReservationValidations },
    ) => {
      state.reservationValidations = action.payload;
    },
    setLoadedBenefitsSummary: (
      state: ReservationState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedBenefitsSummary = action.payload;
    },
    setLoadingBenefitSummary: (
      state: ReservationState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingBenefitSummary = action.payload;
    },
    setPassengerBenefitSummary: (
      state: ReservationState,
      action: { type: string; payload: any },
    ) => {
      state.benefitSummary = action.payload;
    },
    upsertTripLeg: (
      state: ReservationState,
      action: { type: string; payload: TripLeg },
    ) => {
      const newInventory = state.allTripLegs.map((tripLeg) => {
        if (tripLeg.tripLegReferenceID == action.payload.tripLegReferenceID) {
          return action.payload;
        } else {
          return tripLeg;
        }
      });
      if (!newInventory.length) {
        state.allTripLegs = [action.payload];
      } else {
        state.allTripLegs = newInventory;
      }
    },
    setNarrative: (
      state: ReservationState,
      action: { type: string; payload: string | null },
    ) => {
      state.narrative = action.payload;
    },
    setNarrativeMetadata: (
      state: ReservationState,
      action: { type: string; payload: any },
    ) => {
      state.narrativeMetadata = action.payload;
    },
    setReservationEventEmitted: (
      state: ReservationState,
      action: {
        type: string;
        payload: {
          detailType: ReservationEventType;
          detail: ReservationEventDetail;
        } | null;
      },
    ) => {
      state.reservationEventEmitted = action.payload;
    },
    setJustCreatedReservation: (
      state: ReservationState,
      action: {
        type: string;
        payload: {
          passengerUUID: string;
          reservationReferenceID: string;
        } | null;
      },
    ) => {
      state.justCreatedReservation = action.payload;
    },
    setEstimatedTripTime: (
      state: ReservationState,
      action: { type: string; payload: EstimatedPickupTime | null },
    ) => {
      state.estimatedTripTime = action.payload;
    },
    setReservationConfidence: (
      state: ReservationState,
      action: {
        type: string;
        payload: { index: number; confidence: TripLegConfidenceResponse };
      },
    ) => {
      state.reservationConfidence[action.payload.index] =
        action.payload.confidence;
    },
    setLoadingReservationConfidence: (
      state: ReservationState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingReservationConfidence = action.payload;
    },
    setLoadedReservationConfidence: (
      state: ReservationState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedReservationConfidence = action.payload;
    },
    // TODO(dead code): only used by `validateTripLegAddressAsync` (orphaned) — remove together.
    setRequestedReservationConfidence: (
      state: ReservationState,
      action: {
        type: string;
        payload: {
          request: TripLegConfidenceRequest;
          response: TripLegConfidenceResponse | null;
        };
      },
    ) => {
      state.requestedReservationConfidence = [
        ...state.requestedReservationConfidence,
        { ...action.payload },
      ];
    },
    reset: (state: ReservationState) => {
      state.savingReservation = false;
      state.savedReservation = false;
      state.currentReservation = null;
      state.savingTripLeg = false;
      state.savedTripLeg = false;
      state.loadingReservationValidations = false;
      state.loadedReservationValidations = false;
      state.reservationValidations = null;
      state.estimatedTripTime = null;
      state.loadingReservationConfidence = false;
      state.loadedReservationConfidence = false;
      state.requestedReservationConfidence = [];
    },
  },
});

export const { setSavingReservation } = slice.actions;
export const { setSavedReservation } = slice.actions;
export const { setCurrentReservation } = slice.actions;
export const { setLoadingReservation } = slice.actions;
export const { setLoadedReservation } = slice.actions;
export const { setCanceledReservation } = slice.actions;
export const { setSavingTripLeg } = slice.actions;
export const { setSavedTripLeg } = slice.actions;
export const { setCurrentTripLeg } = slice.actions;
export const { setLoadingTripLeg } = slice.actions;
export const { setLoadedTripLeg } = slice.actions;
export const { setLoadingAllTripLegs } = slice.actions;
export const { setLoadedAllTripLegs } = slice.actions;
export const { setCanceledTripLeg } = slice.actions;
export const { setAllTripLegs } = slice.actions;
export const { setLoadingDraftTripLegs } = slice.actions;
export const { setLoadedDraftTripLegs } = slice.actions;
export const { setDraftTripLegs } = slice.actions;
export const { setLoadingReopenRejectedTripLeg } = slice.actions;
export const { setLoadedReopenRejectedTripLeg } = slice.actions;
export const { setLoadedReopenEditTripLegTime } = slice.actions;
export const { setLoadingReservationValidations } = slice.actions;
export const { setLoadedReservationValidations } = slice.actions;
export const { setReservationValidations } = slice.actions;
export const { upsertTripLeg } = slice.actions;
export const { setLoadedBenefitsSummary } = slice.actions;
export const { setLoadingBenefitSummary } = slice.actions;
export const { setPassengerBenefitSummary } = slice.actions;
export const { setNarrative } = slice.actions;
export const { setNarrativeMetadata } = slice.actions;
export const { setReservationEventEmitted } = slice.actions;
export const { setJustCreatedReservation } = slice.actions;
export const { setEstimatedTripTime } = slice.actions;
export const { setReservationConfidence } = slice.actions;
export const { setLoadingReservationConfidence } = slice.actions;
export const { setLoadedReservationConfidence } = slice.actions;
export const { setRequestedReservationConfidence } = slice.actions;
export const { reset } = slice.actions;

export const fetchPassengerBenefitSummaryAsync =
  (
    passengerUUID: string,
    servicePlanUUIDs: string[],
    appointmentDate: string,
  ) =>
  (dispatch: Function) => {
    dispatch(setLoadingBenefitSummary(true));
    reservationService
      .getPassengerBenefitSummary(
        passengerUUID,
        servicePlanUUIDs,
        appointmentDate,
      )
      .subscribe({
        next: (result) => {
          dispatch(setPassengerBenefitSummary(result));
          dispatch(setLoadedBenefitsSummary(true));
          dispatch(setLoadingBenefitSummary(false));
        },
        error: (error: any) => {
          dispatch(setLoadingBenefitSummary(false));
        },
      });
  };

// TODO(dead code): `validateReservationAsync` is orphaned — no component dispatches
// it since the reservation/trip-leg forms migrated to `runBundledValidation`. Only
// its own unit test (reservation.slice.test.ts) references it. Safe to remove
// together with that test. See docs/reservation-validation.md §13.
export const validateReservationAsync =
  (reservation: any) => (dispatch: Function, getState: Function) => {
    dispatch(setLoadingReservationValidations(true));
    dispatch(setLoadedReservationValidations(false));
    const addressValidationEnabled =
      getState()?.user?.loggedInUserInfo?.featureFlags?.includes(
        getAddressValidationFeatureKeyForPortal(getPortalLabel()),
      ) ?? false;
    reservationService.validateReservation(reservation).subscribe({
      next: (validations) => {
        const nextValidations: Omit<
          ReservationValidations,
          'reservationReferenceID'
        > = addressValidationEnabled
          ? {
              ...validations,
              errors: filterAddressErrorsForOneSidedTripLegs(
                filterConfidenceErrorsForUnsetPayload(
                  validations.errors,
                  reservation,
                ),
                reservation,
              ),
            }
          : validations;
        dispatch(
          setReservationValidations({
            reservationReferenceID: reservation.reservationReferenceID,
            ...nextValidations,
          }),
        );
        dispatch(setLoadedReservationValidations(true));
        dispatch(setLoadingReservationValidations(false));
      },
      error: (error) => {
        dispatch(setLoadingReservationValidations(false));
        dispatch(setLoadedReservationValidations(false));
      },
    });
  };

// TODO(dead code): `validateTripLegAddressAsync` is orphaned — its only caller
// (TripLegForm's second /score/trip-leg pass) was removed when the legacy dialogs
// consolidated onto `runBundledValidation`, which now scores every leg once. Remove
// this thunk together with the `requestedReservationConfidence` state,
// `setRequestedReservationConfidence` action, and `selectRequestedReservationConfidence`
// selector, which exist only to dedupe its calls. See docs/reservation-validation.md §13.
export const validateTripLegAddressAsync =
  (index: number, confidencePayload: TripLegConfidenceRequest) =>
  (dispatch: Function, getState: Function) => {
    // check if the confidence payload is already in the requestedReservationConfidence
    const state = getState();
    const isRequested = state.reservation.requestedReservationConfidence.find(
      (rc: {
        request: TripLegConfidenceRequest;
        response: TripLegConfidence | null;
      }) => isEqual(rc.request, confidencePayload),
    );
    if (isRequested) {
      dispatch(
        setReservationConfidence({ index, confidence: isRequested.response }),
      );
      return;
    } else {
      dispatch(setLoadingReservationConfidence(true));
      dispatch(setLoadedReservationConfidence(false));
      triplegService
        .validateTripLegAddress(confidencePayload, {
          throwConfidenceError: false,
        })
        .subscribe({
          next: (confidence) => {
            dispatch(setReservationConfidence({ index, confidence }));
            dispatch(
              setRequestedReservationConfidence({
                request: confidencePayload,
                response: confidence,
              }),
            );
            dispatch(setLoadedReservationConfidence(true));
            dispatch(setLoadingReservationConfidence(false));
          },
          error: (error) => {
            dispatch(setLoadingReservationConfidence(false));
            dispatch(setLoadedReservationConfidence(false));
          },
        });
    }
  };

export const createReservationAsync =
  (
    reservation: Reservation,
    standingOrder: StandingOrder | undefined,
    saveAsDraft: boolean,
  ) =>
  (dispatch: Function) => {
    dispatch(setCurrentReservation(null));
    dispatch(setSavingReservation(true));
    dispatch(setSavedReservation(false));
    reservationService
      .createReservation(reservation, standingOrder, saveAsDraft)
      .subscribe({
        next: (newReservation) => {
          dispatch(
            uiSlice.setSuccessMessage(
              saveAsDraft
                ? 'snackbar.draftReservationSuccess'
                : 'snackbar.reservationSuccess',
            ),
          );
          dispatch(
            passengerSlice.addFuturePassengerReservation(newReservation),
          );
          if (newReservation.reservationReferenceID) {
            dispatch(
              setJustCreatedReservation({
                passengerUUID: reservation.passengerUUID,
                reservationReferenceID: newReservation.reservationReferenceID,
              }),
            );
          }

          dispatch(setSavingReservation(false));
          dispatch(setSavedReservation(true));
        },
        error: (err) => {
          dispatch(
            uiSlice.setErrorMessage(
              err.message || (err.errors && err.errors[0]?.message),
            ),
          );
          dispatch(setSavingReservation(false));
        },
      });
  };

export const cancelReservationAsync =
  (
    passengerUUID: string,
    reservationReferenceID: string,
    tripLegStatusReasonBCs: string[],
  ) =>
  (dispatch: Function) => {
    dispatch(setCanceledReservation(false));
    reservationService
      .cancelReservation(
        passengerUUID,
        reservationReferenceID,
        tripLegStatusReasonBCs,
      )
      .subscribe({
        next: () => {
          dispatch(uiSlice.setSuccessMessage('snackbar.tripCanceled'));
          dispatch(passengerSlice.removeReservation(reservationReferenceID));
          dispatch(setCanceledReservation(true));
        },
        error: (err) => {
          dispatch(
            uiSlice.setErrorMessage('serverError.FAILED_TO_CANCEL_RESERVATION'),
          );
        },
      });
  };

export const cancelStandingOrderReservationAsync =
  (standingOrderUUID: string, tripLegStatusReasonBCs: string[]) =>
  (dispatch: Function) => {
    dispatch(setCanceledReservation(false));
    reservationService
      .cancelStandingOrderReservation(standingOrderUUID, tripLegStatusReasonBCs)
      .subscribe({
        next: () => {
          dispatch(uiSlice.setSuccessMessage('snackbar.tripCanceled'));
          // Optimistic: the backend cancels the occurrences asynchronously
          // (~1-2s after this API returns), so remove the rows locally now;
          // the WebSocket-driven refetch reconciles with server truth.
          dispatch(
            passengerSlice.removeStandingOrderReservations(standingOrderUUID),
          );
          dispatch(setCanceledReservation(true));
        },
        error: (err) => {
          dispatch(
            uiSlice.setErrorMessage('serverError.FAILED_TO_CANCEL_RESERVATION'),
          );
        },
      });
  };

export const getTripLegAsync =
  (passengerUUID: string, reservationID: string, tripLegID: string) =>
  (dispatch: Function) => {
    dispatch(setLoadingTripLeg(true));
    dispatch(setLoadedTripLeg(false));
    reservationService
      .getTripLeg(passengerUUID, reservationID, tripLegID)
      .subscribe({
        next: (newTripLeg) => {
          dispatch(setCurrentTripLeg(newTripLeg));
          dispatch(setLoadingTripLeg(false));
          dispatch(setLoadedTripLeg(true));
        },
        error: (err) => {
          dispatch(setLoadingTripLeg(false));
          if (err.errors || err.warnings) {
            dispatch(setReservationValidations(err));
          }
        },
      });
  };

export const getAllTripLegsAsync =
  (tripLegState: string | null, appointmentDate?: string | null) =>
  (dispatch: Function) => {
    dispatch(setLoadingAllTripLegs(true));
    dispatch(setLoadedAllTripLegs(false));
    reservationService
      .getAllTripLegs(tripLegState, appointmentDate || null)
      .subscribe({
        next: (tripLegs) => {
          dispatch(setLoadingAllTripLegs(false));
          dispatch(setLoadedAllTripLegs(true));
          dispatch(setAllTripLegs(tripLegs.tripLegs));
        },
        error: (err) => {
          dispatch(setLoadingAllTripLegs(false));
          if (err.errors || err.warnings) {
            dispatch(setReservationValidations(err));
          }
        },
      });
  };

export const getDraftTripLegsAsync =
  (tripLegStatus?: string) => (dispatch: Function) => {
    dispatch(setLoadingDraftTripLegs(true));
    dispatch(setLoadedDraftTripLegs(false));
    reservationService.getDraftTripLegs(tripLegStatus).subscribe({
      next: (tripLegs) => {
        dispatch(setLoadingDraftTripLegs(false));
        dispatch(setLoadedDraftTripLegs(true));
        dispatch(setDraftTripLegs(tripLegs.tripLegs));
      },
      error: (err) => {
        dispatch(setLoadingDraftTripLegs(false));
        if (err.errors || err.warnings) {
          dispatch(setReservationValidations(err));
        }
      },
    });
  };

export const reopenRejectedTripLeg =
  (passengerUUID: string, tripLegReferenceID: string) =>
  (dispatch: Function) => {
    dispatch(setLoadingReopenRejectedTripLeg(true));
    dispatch(setLoadedReopenRejectedTripLeg(false));
    reservationService
      .reopenRejectedTripLeg(passengerUUID, tripLegReferenceID)
      .subscribe({
        next: (tripLeg) => {
          dispatch(
            uiSlice.setSuccessMessage('snackbar.tripLegReopenedSuccess'),
          );
          dispatch(tripLegSlice.upsertTripLeg(tripLeg));
          dispatch(tripLegSlice.reopenRejectedTripLeg(tripLegReferenceID));
          dispatch(upsertTripLeg(tripLeg));
          dispatch(setLoadingReopenRejectedTripLeg(false));
          dispatch(setLoadedReopenRejectedTripLeg(true));
        },
        error: (err) => {
          dispatch(uiSlice.setErrorMessage(err.message));
          dispatch(setLoadingReopenRejectedTripLeg(false));
        },
      });
  };

export const addTripLegAsync =
  (reservationID: string, tripLeg: TripLeg, saveAsDraft: boolean) =>
  (dispatch: Function) => {
    dispatch(setSavingTripLeg(true));
    dispatch(setSavedTripLeg(false));
    reservationService
      .addTripLeg(reservationID, tripLeg, saveAsDraft)
      .subscribe({
        next: (newTripLeg) => {
          dispatch(uiSlice.setSuccessMessage('snackbar.addTripLegSuccess'));
          dispatch(
            passengerSlice.addFutureTripLeg({
              reservationReferenceID: reservationID,
              tripLeg: newTripLeg,
            }),
          );

          dispatch(setSavingTripLeg(false));
          dispatch(setSavedTripLeg(true));
        },
        error: (err) => {
          // dispatch(uiSlice.setErrorMessage(err.message));
          dispatch(setSavingTripLeg(false));
          if (err.errors || err.warnings) {
            dispatch(setReservationValidations(err));
          }
        },
      });
  };

export const updateTripLegsAsync =
  (tripLegs: TripLeg[], saveAsDraft: boolean) => (dispatch: Function) => {
    dispatch(setSavingTripLeg(true));
    dispatch(setSavedTripLeg(false));
    reservationService.updateTripLegs(tripLegs, saveAsDraft).subscribe({
      next: (newTripLegs) => {
        dispatch(uiSlice.setSuccessMessage('snackbar.updateTripLegSuccess'));
        dispatch(passengerSlice.updateFutureTripLegs(newTripLegs));
        dispatch(setSavingTripLeg(false));
        dispatch(setSavedTripLeg(true));
      },
      error: (err) => {
        if (err.message) {
          dispatch(uiSlice.setErrorMessage(err.message));
        }
        dispatch(setSavingTripLeg(false));
        if (err.errors || err.warnings) {
          dispatch(setReservationValidations(err));
        }
      },
    });
  };

export const cancelTripLegAsync =
  (
    passengerUUID: string,
    reservationReferenceID: string,
    tripLegReferenceID: string,
    tripLegStatusReasonBCs: string[],
  ) =>
  (dispatch: Function) => {
    dispatch(setCanceledTripLeg(false));
    reservationService
      .cancelTripLeg(
        passengerUUID,
        reservationReferenceID,
        tripLegReferenceID,
        tripLegStatusReasonBCs,
      )
      .subscribe({
        next: (reservation) => {
          dispatch(uiSlice.setSuccessMessage('snackbar.tripLegCanceled'));
          dispatch(
            passengerSlice.replaceReservationAfterCancelTripLeg({
              reservation,
              tripLegReferenceID,
            }),
          );
          dispatch(setCanceledTripLeg(true));
        },
        error: (err) => {
          dispatch(
            uiSlice.setErrorMessage('serverError.FAILED_TO_CANCEL_TRIP_LEG'),
          );
        },
      });
  };

export const reorderTripLegsAsync =
  (passengerUUID: string, reservationReferenceID: string, legs: string[]) =>
  (dispatch: Function) => {
    reservationService
      .reorderTripLegs(passengerUUID, reservationReferenceID, legs)
      .subscribe({
        next: (reservation) => {
          dispatch(passengerSlice.replaceReservation(reservation));
        },
        error: (err) => {
          dispatch(
            uiSlice.setErrorMessage('serverError.FAILED_TO_REORDER_TRIP_LEGS'),
          );
        },
      });
  };

export const approveOrDenyTripLegsAsync =
  (
    passengerUUID: string,
    reservationReferenceID: string,
    tripLegsRefferenceIDs: string[],
    action: OnHoldTripLegApproveDenyAction,
  ) =>
  (dispatch: Function) => {
    dispatch(setSavingReservation(true));
    dispatch(setSavedReservation(false));
    reservationService
      .approveOrDenyTripLegsOnHold(
        passengerUUID,
        reservationReferenceID,
        tripLegsRefferenceIDs,
        action,
      )
      .subscribe({
        next: (reservation) => {
          switch (action) {
            case OnHoldTripLegApproveDenyAction.Approve:
              dispatch(uiSlice.setSuccessMessage('snackbar.tripLegsApproved'));
              break;
            case OnHoldTripLegApproveDenyAction.Deny:
              dispatch(uiSlice.setSuccessMessage('snackbar.tripLegsDenied'));
              break;
            default:
              break;
          }
          if (reservation.state === ReservationModelState.inactive) {
            // when reservation becomes inactive it's considered as past reservation
            // so remove it from future reservations (the reducer matches on the
            // reference ID string, not the reservation object)
            dispatch(
              passengerSlice.removeReservation(
                reservation.reservationReferenceID,
              ),
            );
            // then add it to past reservations
            dispatch(passengerSlice.addPastPassengerReservation(reservation));
          } else {
            dispatch(passengerSlice.replaceReservation(reservation));
          }
          dispatch(setSavingReservation(false));
          dispatch(setSavedReservation(true));
        },
        error: (err) => {
          dispatch(uiSlice.setErrorMessage(err.message));
          dispatch(setSavingReservation(false));
          dispatch(setSavedReservation(false));
        },
      });
  };

export const updateTripLegStatusAsync =
  (
    passengerUUID: string,
    tripLegReferenceID: string,
    action: string,
    tripLegStatusDateTimeUTC: string,
    tripLegStatusReasonBCs?: string[],
  ) =>
  (dispatch: Function) => {
    reservationService
      .updateTripLegStatus(
        passengerUUID,
        tripLegReferenceID,
        action,
        tripLegStatusDateTimeUTC,
        tripLegStatusReasonBCs,
      )
      .subscribe({
        next: (trip: any) => {
          dispatch(uiSlice.setSuccessMessage('snackbar.saveEventTimesSuccess'));
        },
        error: (err) => {
          dispatch(uiSlice.setErrorMessage(err.message));
        },
      });

    // .subscribe({
    //   next: (reservation) => {
    //     dispatch(passengerSlice.replaceReservation(reservation));
    //   },
    //   error: (err) => {
    //     dispatch(
    //       uiSlice.setErrorMessage('serverError.FAILED_TO_REORDER_TRIP_LEGS'),
    //     );
    //   },
    // });
  };

export const getReservationNarrativeAsync =
  (reservation: Reservation, standingOrder: StandingOrder | undefined) =>
  (dispatch: Function) => {
    dispatch(setNarrative(null));
    reservationService
      .getReservationNarrative(reservation, standingOrder)
      .subscribe({
        next: (narrative) => {
          dispatch(setNarrative(narrative.script));
          dispatch(setNarrativeMetadata(narrative.metadata));
        },
        // error: (err) => {
        //   dispatch(
        //     uiSlice.setErrorMessage('serverError.FAILED_TO_REORDER_TRIP_LEGS'),
        //   );
        // },
      });
  };

export const getEstimatedTripTimeAsync =
  (
    pickupAddress: any,
    dropOffAddress: any,
    appointment?:
      | { appointmentDate: string; appointmentTime: string; timeZone: string }
      | { appointmentDateTime: string },
  ) =>
  (dispatch: Function) => {
    dispatch(setEstimatedTripTime(null));
    reservationService
      .getEstimatedTripTime(pickupAddress, dropOffAddress, appointment)
      .subscribe({
        next: (time) => {
          dispatch(setEstimatedTripTime(time));
        },
      });
  };

export const selectSavingReservation = (state: {
  reservation: ReservationState;
}) => state.reservation.savingReservation;
export const selectSavedReservation = (state: {
  reservation: ReservationState;
}) => state.reservation.savedReservation;
export const selectCurrentReservation = (state: {
  reservation: ReservationState;
}) => state.reservation.currentReservation;
export const selectLoadingReservation = (state: {
  reservation: ReservationState;
}) => state.reservation.loadingReservation;
export const selectLoadedReservation = (state: {
  reservation: ReservationState;
}) => state.reservation.loadedReservation;
export const selectCanceledReservation = (state: {
  reservation: ReservationState;
}) => state.reservation.canceledReservation;
export const selectSavingTripLeg = (state: { reservation: ReservationState }) =>
  state.reservation.savingTripLeg;
export const selectSavedTripLeg = (state: { reservation: ReservationState }) =>
  state.reservation.savedTripLeg;
export const selectCurrentTripLeg = (state: {
  reservation: ReservationState;
}) => state.reservation.currentTripLeg;
export const selectLoadingAllTripLegs = (state: {
  reservation: ReservationState;
}) => state.reservation.loadingAllTripLegs;
export const selectLoadedAllTripLegs = (state: {
  reservation: ReservationState;
}) => state.reservation.loadedAllTripLegs;
export const selectCanceledTripLeg = (state: {
  reservation: ReservationState;
}) => state.reservation.canceledTripLeg;
export const selectAllTripLegs = (state: { reservation: ReservationState }) =>
  state.reservation.allTripLegs;
export const selectLoadingDraftTripLegs = (state: {
  reservation: ReservationState;
}) => state.reservation.loadingDraftTripLegs;
export const selectLoadedDraftTripLegs = (state: {
  reservation: ReservationState;
}) => state.reservation.loadedDraftTripLegs;
export const selectDraftTripLegs = (state: { reservation: ReservationState }) =>
  state.reservation.draftTripLegs;
export const selectLoadingReopenRejectedTripLeg = (state: {
  reservation: ReservationState;
}) => state.reservation.loadingReopenRejectedTripLeg;
export const selectLoadedReopenRejectedTripLeg = (state: {
  reservation: ReservationState;
}) => state.reservation.loadedReopenRejectedTripLeg;
export const selectLoadedReopenEditTripLegTime = (state: {
  reservation: ReservationState;
}) => state.reservation.loadedReopenEditTripLegTime;
export const selectReservationValidations = (state: {
  reservation: ReservationState;
}) => state.reservation.reservationValidations;
export const selectLoadingBenefitSummary = (state: {
  reservation: ReservationState;
}) => state.reservation.loadingBenefitSummary;
export const selectLoadedBenefitSummary = (state: {
  reservation: ReservationState;
}) => state.reservation.loadedBenefitsSummary;
export const selectBenefitSummary = (state: {
  reservation: ReservationState;
}) => state.reservation.benefitSummary;
export const selectNarrative = (state: { reservation: ReservationState }) =>
  state.reservation.narrative;
export const selectNarrativeMetadata = (state: {
  reservation: ReservationState;
}) => state.reservation.narrativeMetadata;
export const selectReservationEventEmitted = (state: {
  reservation: ReservationState;
}) => state.reservation.reservationEventEmitted;
export const selectJustCreatedReservation = (state: {
  reservation: ReservationState;
}) => state.reservation.justCreatedReservation;
export const selectEstimatedTripTime = (state: {
  reservation: ReservationState;
}) => state.reservation.estimatedTripTime;
export const selectLoadingReservationValidations = (state: {
  reservation: ReservationState;
}) => state.reservation.loadingReservationValidations;
export const selectLoadedReservationValidations = (state: {
  reservation: ReservationState;
}) => state.reservation.loadedReservationValidations;
export const selectLoadingReservationConfidence = (state: {
  reservation: ReservationState;
}) => state.reservation.loadingReservationConfidence;
export const selectLoadedReservationConfidence = (state: {
  reservation: ReservationState;
}) => state.reservation.loadedReservationConfidence;
export const selectReservationConfidence = (state: {
  reservation: ReservationState;
}) => state.reservation.reservationConfidence;
// TODO(dead code): only used by `validateTripLegAddressAsync` (orphaned) — remove together.
export const selectRequestedReservationConfidence = (state: {
  reservation: ReservationState;
}) => state.reservation.requestedReservationConfidence;

export default slice.reducer;
